'use client';

// RX-AD KdAdSlot 1종 (최종설계안_RX §7 · 판정증분_RX-2). 옛 AdSlot.tsx(AdSense 전용 · Rule #45)는 무접촉 보존.
//
//  ① 고정 높이 예약(렌더 전·후 불변 = CLS 0)  ② 「광고」 라벨 상시  ③ 크리에이티브가 없으면 하우스 폴백(빈 칸 0)
//  ④ 컨테이너 data-nosnippet — 검색 스니펫·발췌 오염 차단, 구조화 데이터에는 넣지 않는다
//  ⑤ 계측 동봉: ad_slot_view(노출 50%·1회) · ad_slot_click → conversion_events (lib/rx/track)
//
// ⛔ 하단 고정 영역(도크·CTA 바)·첫 뷰포트에 두지 않는다 — 호출부 규칙(§7 운영 상한). 이 컴포넌트는 자리만 만든다.
// ⛔ 클로킹 금지 — 서버·클라이언트가 같은 마크업을 그린다(UA·쿠키 분기 없음).

import { useEffect, useRef } from 'react';
import Link from 'next/link';
import { AD_SLOT_SPECS, HOUSE_CREATIVES, isExternalUrl, type AdCreative, type AdSlotId } from '@/lib/rx/ad-slots';
import { trackRx } from '@/lib/rx/track';
import AdvertiseMiniLink from '@/components/ads/acq/AdvertiseMiniLink';
import './kd-ad-slot.css';

export interface KdAdSlotProps {
  slot: AdSlotId;
  /** 서버가 판정한 크리에이티브(pickCreative). 없으면 하우스. */
  creative?: AdCreative | null;
  className?: string;
}

/** 광고 슬롯의 「광고하기」 출처 — 'AD-DETAIL-1' → 'ad_slot_ad_detail_1'(자리마다 다름). */
export const slotAdvertiseFrom = (slot: string): string => `ad_slot_${String(slot).toLowerCase().replace(/[^a-z0-9]+/g, '_')}`.slice(0, 30);

export function KdAdSlot({ slot, creative, className }: KdAdSlotProps) {
  const spec = AD_SLOT_SPECS[slot];
  const c = creative ?? HOUSE_CREATIVES[slot];
  const ref = useRef<HTMLDivElement>(null);
  const seen = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === 'undefined') return;
    const io = new IntersectionObserver((es) => {
      if (seen.current) return;
      if (es.some((e) => e.isIntersecting && e.intersectionRatio >= 0.5)) {
        seen.current = true;
        trackRx('ad_slot_view', slot, c.id);
        io.disconnect();
      }
    }, { threshold: [0.5] });
    io.observe(el);
    return () => io.disconnect();
  }, [slot, c.id]);

  const ext = isExternalUrl(c.targetUrl);
  const linkProps = ext ? { target: '_blank', rel: 'sponsored nofollow noopener' } : {};
  const onClick = () => trackRx('ad_slot_click', slot, c.id);

  return (
    <div
      ref={ref}
      className={['rx-ad', spec.desktopOnly ? 'rx-ad--desk' : '', className].filter(Boolean).join(' ')}
      data-nosnippet
      data-rx-ad-slot={slot}
      data-rx-ad-type={c.type}
      style={{ ['--rx-ad-h-m' as string]: `${spec.reserveMobile}px`, ['--rx-ad-h-d' as string]: `${spec.reserveDesktop}px` }}
    >
      <div className="rx-ad__head"><span className="rx-ad__label" data-rd="label">광고</span>{c.advertiser ? <span className="rx-ad__adv">{c.advertiser}</span> : null}<AdvertiseMiniLink from={slotAdvertiseFrom(slot)} /></div>
      {ext ? (
        <a className="rx-ad__card" href={c.targetUrl} onClick={onClick} {...linkProps}>
          <AdBody c={c} />
        </a>
      ) : (
        <Link className="rx-ad__card" href={c.targetUrl} prefetch={false} onClick={onClick}>
          <AdBody c={c} />
        </Link>
      )}
    </div>
  );
}

function AdBody({ c }: { c: AdCreative }) {
  // VX-2 X8 — 이미지 카드 소재: 주소·alt·고지가 «다» 있을 때만 그림을 그린다(DB 소재의 image_url 만으로는 그리지 않는다 — 종전 그대로).
  //   figure + figcaption(시각 숨김 · caption_ko 전문) + title. 보이는 고지는 sub 줄(「AI 생성 이미지 · …」)이 진다.
  //   48px 고정 상자라 슬롯 예약 높이(AD_SLOT_SPECS) 안에 선다 — 예약 높이·라벨·계측은 그대로다.
  const img = c.imageUrl && c.imageAlt && c.imageCaption ? { src: c.imageUrl, alt: c.imageAlt, cap: c.imageCaption } : null;
  return (
    <>
      {img ? (
        <figure className="rx-ad__fig" data-rx-ad-img="generated">
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img src={img.src} alt={img.alt} title={img.cap} width={48} height={48} loading="lazy" decoding="async" />
          <figcaption className="e-sr">{img.cap}</figcaption>
        </figure>
      ) : null}
      <span className="rx-ad__txt">
        <span className="rx-ad__title">{c.title}</span>
        <span className="rx-ad__sub">{c.sub}</span>
      </span>
      <span className="rx-ad__cta">{c.cta}</span>
    </>
  );
}

export default KdAdSlot;
